/**
 * Loads one page of an inbox list (tracker item 45, phase 4). `fetchPage` must change when the
 * filter changes (wrap it in useCallback); the caller owns the page number and resets it to 1
 * when it changes the filter. A request-id guard stops a slow older answer overwriting a newer one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { PortalPage } from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';

export const INBOX_PAGE_SIZE = 20;

export function useInboxList<T>(fetchPage: (page: number) => Promise<PortalPage<T>>, page: number, failureMessage: string) {
  const [result, setResult] = useState<PortalPage<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const next = await fetchPage(page);
      if (id !== requestId.current) return;
      setResult(next);
    } catch (cause) {
      if (id !== requestId.current) return;
      setError(getErrorMessage(cause, failureMessage));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [fetchPage, page, failureMessage]);

  useEffect(() => { void load(); }, [load]);

  return { rows: result?.data ?? [], pagination: result?.pagination ?? null, loading, error, reload: load };
}
